import { Fragment, useState } from 'react';
import {
   Accordion,
   AccordionItem,
   AccordionIcon,
   AccordionButton,
   AccordionPanel,
   Box,
   Flex,
   Heading,
   Image,
   Text,
   Link,
   Stack,
   Skeleton,
   SimpleGrid,
} from '@chakra-ui/react';
import Pagination from 'Chakra/Pagination';
import { useQuery } from '@tanstack/react-query';
import { Sidebar, SidebarDataType } from '../Sidebar/sidebar';
import { _js } from '@ifixit/localize';

// The newest version of Chakra stopped exporting this type for some reason
// https://github.com/chakra-ui/chakra-ui/discussions/7187
type ExpandedIndex = number | number[];

export function About({
   userid,
   data,
   sidebarData,
}: {
   userid: number;
   data: any;
   sidebarData: SidebarDataType;
}) {
   const wiki = data.about;
   const contributions = data.contributions;

   return (
      <Fragment>
         <Flex direction="column" width="100%" maxW="646px" pl={2}>
            {wiki && <UserWiki data={wiki} />}
            <Contributions data={contributions} userid={userid} />
         </Flex>
         <Sidebar {...sidebarData} />
      </Fragment>
   );
}

const UserWiki = ({ data }: { data: any }) => (
   <Box
      className="renderedText"
      dangerouslySetInnerHTML={{ __html: data }}
      maxW={{ base: '100vw', lg: 'unset' }}
   />
);

type CategoryType = {
   id: string;
   label: string;
   data: any;
};

const Contributions = ({ data, userid }: { data: any; userid: number }) => {
   const categories: CategoryType[] = [
      {
         id: 'private',
         label: _js("Unpublished Guides I've Worked On"),
         data: data.privateGuideData,
      },
      {
         id: 'teardownsAuthored',
         label: _js('My Teardowns'),
         data: data.teardownsAuthored,
      },
      {
         id: 'teardownsContributed',
         label: _js("Teardowns I've Contributed To"),
         data: data.teardownsContributed,
      },
      {
         id: 'teardownsTranslated',
         label: _js("Teardowns I've Helped Translate"),
         data: data.teardownsTranslated,
      },
      { id: 'guidesAuthored', label: _js('My Guides'), data: data.authored },
      {
         id: 'guidesContributed',
         label: _js("Guides I've Contributed To"),
         data: data.contributed,
      },
      {
         id: 'guidesTranslated',
         label: _js("Guides I've Helped Translate"),
         data: data.translated,
      },
      {
         id: 'wikisAuthored',
         label: _js('My Wikis'),
         data: data.wikisAuthored,
      },
      {
         id: 'wikisContributed',
         label: _js("Wikis I've Contributed To"),
         data: data.wikisContributed,
      },
      {
         id: 'wikisTranslated',
         label: _js("Wikis I've Helped Translate"),
         data: data.wikisTranslated,
      },
      { id: 'favoritedGuides', label: _js('My Favorite Guides'), data: data.favorites },
      { id: 'success', label: _js('Completed Guides'), data: data.successes },
      { id: 'stories', label: _js('My Stories'), data: data.stories },
      { id: 'questions', label: _js('Questions'), data: data.questions },
      { id: 'answers', label: _js('Answers'), data: data.answers },
      { id: 'comments', label: _js('Guide Comments'), data: data.comments },
   ];

   function showBlurb(blurbData: any) {
      return (
         blurbData &&
         ((blurbData.blurbs && blurbData.blurbs.length > 0) ||
            (blurbData.info && blurbData.info.length > 0))
      );
   }

   return (
      <Fragment>
         {categories.map((category: CategoryType) =>
            showBlurb(category.data) ? (
               <GuideBlurb key={category.id} category={category} userid={userid} />
            ) : null
         )}
      </Fragment>
   );
};

const GuideBlurb = ({ category, userid }: { category: CategoryType; userid: number }) => {
   const data = category.data;
   const pageContext = data.pageContext;
   return (
      <Box key={category.id} mt="12px" w="100%">
         <Heading
            as="h2"
            id={category.id}
            fontSize="22px"
            fontWeight="semibold"
            borderBottomWidth="4px"
            borderColor="gray.100"
         >
            {category.label}
         </Heading>
         {data.blurbs ? <BlurbGrid initialPageContext={pageContext} userid={userid} /> : null}
         {data.info ? <BlurbList initialPageContext={pageContext} userid={userid} /> : null}
      </Box>
   );
};

const BlurbGrid = ({ initialPageContext, userid }: { initialPageContext: any; userid: number }) => {
   const [pageContext, setPageContext] = useState(initialPageContext);
   const { data, isLoading, error } = useQuery({
      queryKey: [`users/${userid}/blurb/${initialPageContext.type}/${pageContext.curPage}`],
   });
   const showPagination = initialPageContext.totalPages > 1;
   const MAX_ITEMS = 10;

   if (error) {
      return <BlurbErrorMessage />;
   }

   return (
      <Fragment>
         <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacingX="12px"
            spacingY="8px"
            mt="8px"
            templateRows={showPagination ? 'repeat(5,1fr)' : 'auto'}
            mb={showPagination ? '8px' : '0'}
            minH={showPagination ? '383px' : 'unset'}
         >
            {isLoading ? (
               <BlurbGridSkeleton length={MAX_ITEMS} />
            ) : (
               <BlurbGridContent data={data} />
            )}
         </SimpleGrid>
         {showPagination && (
            <Pagination pageContext={pageContext} setPageContext={setPageContext} />
         )}
      </Fragment>
   );
};

const BlurbGridSkeleton = ({ length }: { length: number }) => (
   <Fragment>
      {Array.from({ length: length }, (e, i) => (
         <Skeleton key={i} height="67px" endColor="gray.300" borderRadius="4px" />
      ))}
   </Fragment>
);

const BlurbGridContent = ({ data }: { data: any }) => (
   <Fragment>
      {data.blurbs.map((blurb: any) => (
         <BlurbGridItem key={blurb.title} blurb={blurb} />
      ))}
   </Fragment>
);

const BlurbGridItem = ({ blurb }: { blurb: any }) => (
   <Flex
      p="5px"
      h="67px"
      align="center"
      borderWidth={1}
      borderColor="gray.300"
      bgColor="gray.100"
      borderRadius="4px"
      transition="all 0.5s"
      _hover={{
         bg: 'gray.200',
      }}
   >
      <Image
         borderWidth={1}
         borderColor="gray.300"
         h="100%"
         src={blurb.thumbnail}
         alt={blurb.device ? blurb.device : blurb.title}
      />
      <Link
         color="brand.500"
         fontWeight="semibold"
         fontSize="14px"
         ml="8px"
         href={blurb.guideUrl}
         maxHeight="60px"
         overflow="hidden"
      >
         {blurb.title}
      </Link>
   </Flex>
);

const BlurbList = ({ initialPageContext, userid }: { initialPageContext: any; userid: number }) => {
   const [pageContext, setPageContext] = useState(initialPageContext);
   const [expanded, setExpanded] = useState<ExpandedIndex[]>([]);
   const { data, isLoading, error } = useQuery({
      queryKey: [`users/${userid}/blurb/${initialPageContext.type}/${pageContext.curPage}`],
   });

   const showPagination = initialPageContext.totalPages > 1;
   const MAX_ITEMS = 10;

   function paginationHandler() {
      setExpanded([]);
   }

   if (error) {
      return <BlurbErrorMessage />;
   }

   return (
      <Fragment>
         {isLoading ? (
            <BlurbListSkeleton length={MAX_ITEMS} showPagination={showPagination} />
         ) : (
            <BlurbListContent
               data={data}
               expanded={expanded}
               setExpanded={setExpanded}
               showPagination={showPagination}
            />
         )}
         {showPagination && (
            <Pagination
               pageContext={pageContext}
               setPageContext={setPageContext}
               handler={paginationHandler}
            />
         )}
      </Fragment>
   );
};

const BlurbListSkeleton = ({
   length,
   showPagination,
}: {
   length: number;
   showPagination: boolean;
}) => (
   <Stack pt="6px" spacing="8px" minH={showPagination ? '288px' : '280px'}>
      {Array.from({ length: length }, (e, i) => (
         <Skeleton key={i} height="19px" endColor="gray.300" />
      ))}
   </Stack>
);

const BlurbListContent = ({
   data,
   expanded,
   setExpanded,
   showPagination,
}: {
   data: any;
   expanded: ExpandedIndex[];
   setExpanded: (_expandedIndex: ExpandedIndex[]) => void;
   showPagination: boolean;
}) => {
   if (!data.pageContext || !data.pageContext.type || !data.info) {
      return null;
   }

   return (
      <Accordion
         allowToggle
         index={expanded as number[]}
         id={data.pageContext.type + 'Div'}
         onChange={expandedIndex => setExpanded([expandedIndex])}
         mb={showPagination ? '8px' : '0'}
         w="100%"
         minH={showPagination ? '280px' : 'unset'}
      >
         {data.info.map((info: any, index: any) => (
            <BlurbListItem key={info.title + index} info={info} />
         ))}
      </Accordion>
   );
};

const BlurbListItem = ({ info }: { info: any }) => (
   <AccordionItem border="none" maxW="100%">
      <AccordionButton p="4px 0">
         <Flex color="brand.500" w="100%">
            <AccordionIcon mr="4px" />
            <Text
               m="0"
               textAlign="left"
               pr="8px"
               fontWeight="semibold"
               whiteSpace={{ base: 'unset', md: 'nowrap' }}
               overflow="hidden"
               textOverflow="ellipsis"
            >
               {info.title}
            </Text>
            <Box flexGrow={2} />
            {info.date_html && (
               <Box
                  mr="2px"
                  color="gray.400"
                  fontStyle="italic"
                  whiteSpace="nowrap"
                  dangerouslySetInnerHTML={{ __html: info.date_html }}
               />
            )}
         </Flex>
      </AccordionButton>
      <AccordionPanel fontSize="14px">
         <Box dangerouslySetInnerHTML={{ __html: info.text }} />
         <Link mt="4px" fontWeight="semibold" href={info.url} color="brand.500">
            {_js('Read more')}
         </Link>
      </AccordionPanel>
   </AccordionItem>
);

const BlurbErrorMessage = () => <Box>{_js('Something went wrong.')}</Box>;
